import type {
  DeviationEvent,
  DeviationRun,
  EdgeRole,
  EngineEvaluation,
  ExplorerMove,
  Game,
  GameSummary,
  Job,
  NodeRef,
  PositionNode,
  ProviderAccount,
  Repertoire,
  RepertoireEdge,
  RepertoireLine,
  Transition,
  UciMove,
} from '../api/types';

/**
 * In-memory §4 mock dataset. It encodes the spec's own golden transposition: the
 * corrected Caro-Kann pair where 2.d4/d5/Nc3 and 2.Nc3/d5/d4 reach the SAME
 * canonical node after 3...dxe4 (and after 4.Nxe4). The two lines therefore
 * share canonical nodes — exactly the node-vs-path distinction the UI models.
 */

function uuid(n: number): string {
  return `00000000-0000-4000-8000-${n.toString(16).padStart(12, '0')}`;
}

function nodeRef(id: number): NodeRef {
  return { hashVersion: 1, zobristHash: id.toString(16).padStart(32, '0'), positionId: String(id) };
}

interface PosDef {
  id: number;
  fen: string;
  sideToMove: 'w' | 'b';
}

const POS: PosDef[] = [
  { id: 1, fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', sideToMove: 'w' },
  { id: 2, fen: 'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq - 0 1', sideToMove: 'b' },
  { id: 3, fen: 'rnbqkbnr/pp1ppppp/2p5/8/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2', sideToMove: 'w' },
  { id: 4, fen: 'rnbqkbnr/pp1ppppp/2p5/8/3PP3/8/PPP2PPP/RNBQKBNR b KQkq - 0 2', sideToMove: 'b' },
  { id: 5, fen: 'rnbqkbnr/pp2pppp/2p5/3p4/3PP3/8/PPP2PPP/RNBQKBNR w KQkq - 0 3', sideToMove: 'w' },
  { id: 6, fen: 'rnbqkbnr/pp2pppp/2p5/3p4/3PP3/2N5/PPP2PPP/R1BQKBNR b KQkq - 0 3', sideToMove: 'b' },
  { id: 7, fen: 'rnbqkbnr/pp2pppp/2p5/8/3Pp3/2N5/PPP2PPP/R1BQKBNR w KQkq - 0 4', sideToMove: 'w' },
  { id: 8, fen: 'rnbqkbnr/pp2pppp/2p5/8/3PN3/8/PPP2PPP/R1BQKBNR b KQkq - 0 4', sideToMove: 'b' },
  { id: 9, fen: 'rnbqkbnr/pp1ppppp/2p5/8/4P3/2N5/PPPP1PPP/R1BQKBNR b KQkq - 0 2', sideToMove: 'b' },
  { id: 10, fen: 'rnbqkbnr/pp2pppp/2p5/3p4/4P3/2N5/PPPP1PPP/R1BQKBNR w KQkq - 0 3', sideToMove: 'w' },
];

function position(def: PosDef): PositionNode {
  const ref = nodeRef(def.id);
  return {
    hashVersion: ref.hashVersion,
    zobristHash: ref.zobristHash,
    positionId: ref.positionId,
    canonicalFen: def.fen,
    sideToMove: def.sideToMove,
    castling: 'KQkq',
    enPassantFile: null,
  };
}

export const positions: PositionNode[] = POS.map(position);
export const positionById = new Map(positions.map((p) => [p.positionId, p]));

interface MoveDef {
  uci: UciMove;
  san: string;
  from: number;
  to: number;
  role: EdgeRole;
  comment?: string;
}

const LINE_A: MoveDef[] = [
  { uci: 'e2e4', san: 'e4', from: 1, to: 2, role: 'main' },
  { uci: 'c7c6', san: 'c6', from: 2, to: 3, role: 'main' },
  { uci: 'd2d4', san: 'd4', from: 3, to: 4, role: 'main', comment: 'Main line: advance' },
  { uci: 'd7d5', san: 'd5', from: 4, to: 5, role: 'main' },
  { uci: 'b1c3', san: 'Nc3', from: 5, to: 6, role: 'main' },
  { uci: 'd5e4', san: 'dxe4', from: 6, to: 7, role: 'main' },
  { uci: 'c3e4', san: 'Nxe4', from: 7, to: 8, role: 'main' },
];

const LINE_B: MoveDef[] = [
  { uci: 'e2e4', san: 'e4', from: 1, to: 2, role: 'main' },
  { uci: 'c7c6', san: 'c6', from: 2, to: 3, role: 'main' },
  { uci: 'b1c3', san: 'Nc3', from: 3, to: 9, role: 'alternative', comment: 'Alternative: develop first' },
  { uci: 'd7d5', san: 'd5', from: 9, to: 10, role: 'main' },
  { uci: 'd2d4', san: 'd4', from: 10, to: 6, role: 'main' },
  { uci: 'd5e4', san: 'dxe4', from: 6, to: 7, role: 'main' },
  { uci: 'c3e4', san: 'Nxe4', from: 7, to: 8, role: 'main' },
];

const edgeKey = (m: MoveDef): string => `${m.from}-${m.to}`;

function buildEdges(): { edges: RepertoireEdge[]; byKey: Map<string, RepertoireEdge> } {
  const byKey = new Map<string, RepertoireEdge>();
  let edgeCounter = 100;
  let transitionCounter = 1;
  const transitionIds = new Map<string, string>();

  for (const m of [...LINE_A, ...LINE_B]) {
    const key = edgeKey(m);
    if (!byKey.has(key)) {
      const transitionId = transitionIds.get(key) ?? String(transitionCounter++);
      transitionIds.set(key, transitionId);
      const transition: Transition = {
        transitionId,
        uci: m.uci,
        san: m.san,
        from: nodeRef(m.from),
        to: nodeRef(m.to),
      };
      byKey.set(key, {
        edgeId: uuid(edgeCounter++),
        transition,
        role: m.role,
        priority: m.role === 'main' ? 100 : 50,
        enabled: true,
        comment: m.comment ?? null,
        maxLossCp: null,
      });
    }
  }
  return { edges: [...byKey.values()], byKey };
}

const { edges, byKey: edgeByKey } = buildEdges();

export { edgeByKey };

export const repertoire: Repertoire = {
  repertoireId: uuid(2),
  workspaceId: uuid(1),
  name: 'Caro-Kann',
  side: 'black',
  visibility: 'private',
  version: '7',
  defaultRoot: {
    rootId: uuid(3),
    name: 'Start position',
    position: nodeRef(1),
    default: true,
  },
  settings: {},
  createdAt: '2025-01-01T00:00:00Z',
  updatedAt: '2025-01-02T00:00:00Z',
};

function buildLine(id: number, name: string, moves: MoveDef[]): RepertoireLine {
  return {
    lineId: uuid(id),
    repertoireId: repertoire.repertoireId,
    rootId: repertoire.defaultRoot.rootId,
    name,
    movesUci: moves.map((m) => m.uci),
    movesSan: moves.map((m) => m.san),
    nodes: [nodeRef(moves[0].from), ...moves.map((m) => nodeRef(m.to))],
    edgeIds: moves.map((m) => edgeByKey.get(edgeKey(m))!.edgeId),
    active: true,
    createdAt: '2025-01-01T00:00:00Z',
  };
}

export const lineA = buildLine(10, 'Caro-Kann main', LINE_A);
export const lineB = buildLine(11, 'Caro-Kann (Nc3 first)', LINE_B);
export const lines: RepertoireLine[] = [lineA, lineB];

const incomingPathCount = new Map<number, number>([
  [1, 0], [2, 1], [3, 1], [4, 1], [5, 1], [6, 2], [7, 2], [8, 2], [9, 1], [10, 1],
]);

const minMaxPly = new Map<number, [number, number]>([
  [1, [0, 0]], [2, [1, 1]], [3, [2, 2]], [4, [3, 3]], [5, [4, 4]],
  [6, [5, 5]], [7, [6, 6]], [8, [7, 7]], [9, [3, 3]], [10, [4, 4]],
]);

export function outgoingFor(positionId: string): RepertoireEdge[] {
  const id = Number(positionId);
  return edges.filter((e) => e.transition.from.positionId === String(id));
}

export function nodeInfo(positionId: string): {
  position: PositionNode;
  outgoing: RepertoireEdge[];
  incomingPathCount: number;
  minPly: number;
  maxPly: number;
} {
  const id = Number(positionId);
  const [minPly, maxPly] = minMaxPly.get(id) ?? [0, 0];
  return {
    position: positionById.get(String(id))!,
    outgoing: outgoingFor(String(id)),
    incomingPathCount: incomingPathCount.get(id) ?? 0,
    minPly,
    maxPly,
  };
}

export const mockJob: Job = {
  jobId: uuid(50),
  kind: 'game_import',
  status: 'running',
  progress: { completed: '1', total: '3' },
  attempts: 0,
  result: null,
  error: null,
  createdAt: '2025-01-03T00:00:00Z',
  startedAt: '2025-01-03T00:00:01Z',
  completedAt: null,
};

export const mockJobEvents: Array<{ id: string; event: string; data: unknown }> = [
  { id: '1', event: 'progress', data: { completed: '1', total: '3' } },
  { id: '2', event: 'progress', data: { completed: '2', total: '3' } },
  { id: '3', event: 'progress', data: { completed: '3', total: '3' } },
  { id: '4', event: 'complete', data: { status: 'succeeded' } },
];

export const mockEvaluation: EngineEvaluation = {
  evaluationId: uuid(60),
  position: nodeRef(1),
  engineBuild: 'stockfish-17-dev',
  optionsHash: '0'.repeat(32),
  limit: { kind: 'depth', value: '18' },
  multiPv: 1,
  lines: [
    {
      rank: 1,
      score: { type: 'cp', value: 24 },
      perspective: 'side_to_move',
      depth: 18,
      nodes: '123456',
      timeMs: 120,
      bestMove: 'e2e4',
      pv: ['e2e4', 'c7c5'],
    },
  ],
  completedAt: '2025-01-03T00:00:00Z',
};

export const mockGames: GameSummary[] = [
  {
    gameId: uuid(70),
    source: 'pgn',
    white: 'Alice',
    black: 'You',
    result: '0-1',
    playedAt: '2025-01-03T00:00:00Z',
    plyCount: 6,
  },
  {
    gameId: uuid(71),
    source: 'lichess',
    white: 'Bob',
    black: 'You',
    result: '1/2-1/2',
    playedAt: '2025-01-02T00:00:00Z',
    plyCount: 8,
  },
  {
    gameId: uuid(72),
    source: 'chesscom',
    white: 'You',
    black: 'Carol',
    result: '1-0',
    playedAt: '2025-01-01T00:00:00Z',
    plyCount: 10,
  },
];

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/** Game 1: 1.e4 c6 2.d4 Nf6?! leaves the prepared 2...d5 (then 3.e5 Nd5). */
export const mockGame: Game = {
  ...mockGames[0],
  initialPosition: nodeRef(1),
  initialFen: START_FEN,
  userColor: 'b',
  movesUci: ['e2e4', 'c7c6', 'd2d4', 'g8f6', 'e4e5', 'f6d5'],
};

/** Game 2: 1.Nc3 leaves book, 2.e4 transposes back in, prep ends after 4.Nxe4. */
export const mockTranspositionGame: Game = {
  ...mockGames[1],
  initialPosition: nodeRef(1),
  initialFen: START_FEN,
  userColor: 'b',
  movesUci: ['b1c3', 'c7c6', 'e2e4', 'd7d5', 'd2d4', 'd5e4', 'c3e4', 'c8f5'],
};

/** Game 3: a White London game — it matches only a White repertoire someone builds. */
export const mockLondonGame: Game = {
  ...mockGames[2],
  initialPosition: nodeRef(1),
  initialFen: START_FEN,
  userColor: 'w',
  movesUci: ['d2d4', 'd7d5', 'c1f4', 'b8c6', 'e2e3', 'g8f6', 'c2c4', 'e7e6', 'b1c3', 'f8e7'],
};

export const mockGamesById = new Map<string, Game>([
  [mockGame.gameId, mockGame],
  [mockTranspositionGame.gameId, mockTranspositionGame],
  [mockLondonGame.gameId, mockLondonGame],
]);

export const mockProviderAccounts: ProviderAccount[] = [
  {
    providerAccountId: uuid(80),
    provider: 'chesscom',
    username: 'chesslearner',
    status: 'active',
    connectionId: null,
    lastSyncAt: '2025-01-03T00:00:00Z',
    syncCursorAt: '2025-01-02T21:14:00Z',
    lastErrorCode: null,
    gamesSynced: '42',
    createdAt: '2024-12-01T00:00:00Z',
  },
];

export const mockDeviationRun: DeviationRun = {
  runId: uuid(80),
  gameId: uuid(70),
  repertoireId: repertoire.repertoireId,
  algorithmVersion: 3,
  status: 'succeeded',
  eventCount: 3,
  createdAt: '2025-01-03T00:00:00Z',
  completedAt: '2025-01-03T00:00:02Z',
};

/** Positions after leaving the fixture repertoire (never repertoire members). */
const offBook = (n: number): NodeRef => nodeRef(900 + n);

/**
 * Events exactly as the §5.3 detector emits them: once a game leaves the
 * repertoire, EVERY following ply is a (opponent_)novelty until it re-enters.
 */
export const mockDeviationEvents: DeviationEvent[] = [
  {
    eventId: uuid(81), ply: 3, kind: 'novelty', source: nodeRef(4), target: offBook(1),
    actualMove: 'g8f6', expectedMoves: ['d7d5'], lossCp: null, explanation: null,
  },
  {
    eventId: uuid(82), ply: 4, kind: 'opponent_novelty', source: offBook(1), target: offBook(2),
    actualMove: 'e4e5', expectedMoves: [], lossCp: null, explanation: null,
  },
  {
    eventId: uuid(83), ply: 5, kind: 'novelty', source: offBook(2), target: offBook(3),
    actualMove: 'f6d5', expectedMoves: [], lossCp: null, explanation: null,
  },
];

export const mockTranspositionRun: DeviationRun = {
  ...mockDeviationRun,
  runId: uuid(84),
  gameId: uuid(71),
  eventCount: 4,
};

export const mockTranspositionEvents: DeviationEvent[] = [
  {
    eventId: uuid(85), ply: 0, kind: 'opponent_novelty', source: nodeRef(1), target: offBook(4),
    actualMove: 'b1c3', expectedMoves: ['e2e4'], lossCp: null, explanation: null,
  },
  {
    eventId: uuid(86), ply: 1, kind: 'novelty', source: offBook(4), target: offBook(5),
    actualMove: 'c7c6', expectedMoves: [], lossCp: null, explanation: null,
  },
  {
    eventId: uuid(87), ply: 2, kind: 'transposition_reentry', source: offBook(5), target: nodeRef(9),
    actualMove: 'e2e4', expectedMoves: [], lossCp: null, explanation: null,
  },
  {
    eventId: uuid(88), ply: 7, kind: 'novelty', source: nodeRef(8), target: offBook(6),
    actualMove: 'c8f5', expectedMoves: [], lossCp: null, explanation: null,
  },
];

export const mockDeviationRuns = new Map<string, { run: DeviationRun; events: DeviationEvent[] }>([
  [mockDeviationRun.runId, { run: mockDeviationRun, events: mockDeviationEvents }],
  [mockTranspositionRun.runId, { run: mockTranspositionRun, events: mockTranspositionEvents }],
]);

export const mockExplorerMoves: ExplorerMove[] = [
  { san: 'e4', uci: 'e2e4', games: 26, whiteWins: 9, draws: 7, blackWins: 10 },
  { san: 'd4', uci: 'd2d4', games: 11, whiteWins: 4, draws: 3, blackWins: 4 },
  { san: 'Nf3', uci: 'g1f3', games: 5, whiteWins: 2, draws: 1, blackWins: 2 },
];
